/**
 * Edit hub upload doc_type (+ optional insurance expiry / invoice amount).
 * After save: Saved badge + Edit + toast (LienTab #337 pattern).
 */
import { useRef, useState } from 'react'
import { HUB_DOC_TYPES } from './DocUploadModal'
import DocTypeEditFields from './DocTypeEditFields'
import SavedBadge from '../SavedBadge'
import { useActionToast } from '../../lib/useActionToast'

function isMoneyType(t: string) {
  return t === 'invoice' || t === 'change_order'
}

function amountMissing(raw: string | number | null | undefined): boolean {
  if (raw == null || raw === '') return true
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw))
  return !Number.isFinite(n) || n === 0
}

export interface DocTypeEditModalProps {
  currentType: string
  currentExpires?: string | null
  currentAmount?: string | number | null
  fileName?: string | null
  onClose: () => void
  onSave: (
    docType: string,
    insuranceExpires: string | null,
    amount?: number | null,
  ) => Promise<void>
}

function toDateInput(raw: string | null | undefined): string {
  if (!raw) return ''
  return String(raw).slice(0, 10)
}

function typeLabel(value: string): string {
  return HUB_DOC_TYPES.find((t) => t.value === value)?.label ?? value
}

function initialAmount(raw: string | number | null | undefined): string {
  return amountMissing(raw) ? '' : String(raw)
}

export default function DocTypeEditModal({
  currentType,
  currentExpires,
  currentAmount,
  fileName,
  onClose,
  onSave,
}: DocTypeEditModalProps) {
  const { showToast, Toast } = useActionToast()
  const saveInFlight = useRef(false)
  const [docType, setDocType] = useState(currentType || 'other')
  const [expiresOn, setExpiresOn] = useState(toDateInput(currentExpires))
  const [amountStr, setAmountStr] = useState(initialAmount(currentAmount))
  const [baseline, setBaseline] = useState({
    docType: currentType || 'other',
    expiresOn: toDateInput(currentExpires),
    amountStr: initialAmount(currentAmount),
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [hasConfirmedSave, setHasConfirmedSave] = useState(false)

  const isInsurance = docType === 'insurance'
  const showAmount = isMoneyType(docType)
  const needAmount = showAmount && amountMissing(baseline.amountStr || null)

  const dirty =
    docType !== baseline.docType
    || (isInsurance ? expiresOn : '') !== (baseline.docType === 'insurance' ? baseline.expiresOn : '')
    || (showAmount ? amountStr.trim() : '')
      !== (isMoneyType(baseline.docType) ? baseline.amountStr.trim() : '')

  const showSavedView = hasConfirmedSave && !editing && !dirty

  async function submit() {
    if (saveInFlight.current || busy || !dirty) return
    setError(null)
    saveInFlight.current = true
    setBusy(true)
    try {
      let amount: number | null | undefined
      if (showAmount) {
        const trimmed = amountStr.trim()
        if (!trimmed) {
          if (needAmount) {
            setError('Enter an amount')
            return
          }
          amount = undefined
        } else {
          const n = parseFloat(trimmed.replace(/[$,\s]/g, ''))
          if (!Number.isFinite(n) || n <= 0) {
            setError('Enter a valid amount')
            return
          }
          amount = Math.round(n * 100) / 100
        }
      }
      await onSave(docType, isInsurance ? (expiresOn || null) : null, amount)
      const next = {
        docType,
        expiresOn: isInsurance ? expiresOn : '',
        amountStr: showAmount && amount != null ? String(amount) : (showAmount ? amountStr : ''),
      }
      setBaseline(next)
      if (showAmount && amount != null) setAmountStr(String(amount))
      setHasConfirmedSave(true)
      setEditing(false)
      showToast('Document type saved')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusy(false)
      saveInFlight.current = false
    }
  }

  return (
    <div
      data-testid="hub-doc-type-edit-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => { if (!busy) onClose() }}
    >
      {Toast}
      <div
        style={{
          background: 'var(--surface)', borderRadius: 'var(--r)',
          padding: 24, maxWidth: 400, width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>Edit document</h3>
        {fileName ? (
          <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--ink3)' }}>{fileName}</p>
        ) : null}

        {showSavedView ? (
          <div
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}
            data-testid="hub-doc-type-saved"
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
              {typeLabel(baseline.docType)}
            </span>
            <SavedBadge testId="hub-doc-type-saved-badge" />
            <button
              type="button"
              className="btn b-ol btn-sm"
              data-testid="hub-doc-type-edit"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
            <button type="button" className="btn b-dk btn-sm" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <DocTypeEditFields
            docType={docType}
            setDocType={(v) => { setDocType(v); setEditing(true) }}
            amountStr={amountStr}
            setAmountStr={(v) => { setAmountStr(v); setEditing(true) }}
            expiresOn={expiresOn}
            setExpiresOn={(v) => { setExpiresOn(v); setEditing(true) }}
            showAmount={showAmount}
            needAmount={needAmount}
            isInsurance={isInsurance}
            error={error}
            busy={busy}
            dirty={dirty}
            onClose={onClose}
            onSave={() => void submit()}
          />
        )}
      </div>
    </div>
  )
}
